import {
  X,
  Settings2,
  ChevronDown,
  Minus,
  Plus,
  Coins,
  Square,
  RectangleVertical,
  Smartphone,
  Monitor,
} from "lucide-react";
import type { SelectedStyleConfig } from "./types";

interface AdStudioSidebarProps {
  selectedStyles: SelectedStyleConfig[];
  expandedConfigs: Set<string>;
  onRemoveStyle: (styleId: string) => void;
  onToggleConfig: (styleId: string) => void;
  onUpdateConfig: (styleId: string, updates: Partial<SelectedStyleConfig>) => void;
}

export function AdStudioSidebar({
  selectedStyles,
  expandedConfigs,
  onRemoveStyle,
  onToggleConfig,
  onUpdateConfig,
}: AdStudioSidebarProps) {
  return (
    <div className="w-80 border-l border-gray-100 flex flex-col">
      <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">TUS ESTILOS</h3>
        {selectedStyles.length > 0 && (
          <span className="text-sm text-gray-500">{selectedStyles.length} estilos</span>
        )}
      </div>
      <div className="flex-1 overflow-y-auto px-6 py-4">
        {selectedStyles.length === 0 ? (
          <p className="text-sm text-gray-500 text-center mt-8">
            Selecciona estilos de la galería
          </p>
        ) : (
          <div className="space-y-3">
            {selectedStyles.map((config) => {
              const isExpanded = expandedConfigs.has(config.style.id);
              const styleName = config.style.name || `Estilo ${config.style.id}`;
              const creditsPerImage = 20;
              const totalCredits = config.quantity * creditsPerImage;

              return (
                <div
                  key={config.style.id}
                  className="rounded-xl border border-gray-200/60 bg-white overflow-hidden"
                >
                  {/* Style Card Header - Horizontal Layout */}
                  <div className="flex items-center gap-3 p-2.5">
                    {/* Thumbnail */}
                    <div className="w-10 h-10 rounded-lg overflow-hidden bg-gray-100/20 flex-shrink-0">
                      {/* <img
                        src={config.style.image_url}
                        alt={styleName}
                        className="w-full h-full object-cover"
                      /> */}
                    </div>

                    {/* Style Name */}
                    <span className="text-sm font-medium text-gray-900 truncate flex-1 min-w-0">
                      {styleName}
                    </span>

                    {/* Remove Button */}
                    <button
                      type="button"
                      onClick={() => onRemoveStyle(config.style.id)}
                      className="w-6 h-6 rounded-md flex items-center justify-center text-gray-400/50 hover:text-red-600 hover:bg-red-50 transition-colors flex-shrink-0"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>

                  {/* Configuration Section */}
                  <div className="px-2.5 pb-2">
                    <div className="mt-1.5">
                      <button
                        type="button"
                        onClick={() => onToggleConfig(config.style.id)}
                        className="w-full flex items-center gap-1.5 py-1.5 px-2 rounded-lg text-[11px] font-medium transition-colors text-purple-600 bg-purple-50"
                      >
                        <Settings2 className="h-3 w-3" />
                        <span>Configuración</span>
                        <ChevronDown
                          className={`h-3 w-3 ml-auto transition-transform duration-200 ${
                            isExpanded ? "rotate-180" : ""
                          }`}
                        />
                      </button>

                      {/* Expanded Configuration */}
                      {isExpanded && (
                        <div className="overflow-hidden">
                          <div className="pt-2.5 pb-1 px-1 space-y-3">
                            {/* Format */}
                            <div>
                              <p className="text-[10px] font-semibold text-gray-500 mb-1.5 uppercase tracking-wider">
                                Formato
                              </p>
                              <div className="grid grid-cols-4 gap-1.5">
                                {([
                                  { format: "1:1" as const, icon: Square },
                                  { format: "4:5" as const, icon: RectangleVertical },
                                  { format: "9:16" as const, icon: Smartphone },
                                  { format: "16:9" as const, icon: Monitor },
                                ] as const).map(({ format, icon: Icon }) => (
                                  <button
                                    key={format}
                                    type="button"
                                    onClick={() => onUpdateConfig(config.style.id, { format })}
                                    className={`flex flex-col items-center gap-1 py-2 rounded-lg border transition-all duration-150 ${
                                      config.format === format
                                        ? "border-purple-600 bg-purple-50 text-purple-600"
                                        : "border-gray-200/60 text-gray-500 hover:border-gray-300"
                                    }`}
                                  >
                                    <Icon className="h-3.5 w-3.5" strokeWidth={1.5} />
                                    <span className="text-[10px] font-semibold">{format}</span>
                                  </button>
                                ))}
                              </div>
                            </div>
                            {/* Instructions */}
                            <div>
                              <p className="text-[10px] font-semibold text-gray-500 mb-1.5 uppercase tracking-wider">
                                Instrucciones
                              </p>
                              <textarea
                                value={config.instructions}
                                onChange={(e) =>
                                  onUpdateConfig(config.style.id, {
                                    instructions: e.target.value,
                                  })
                                }
                                placeholder="Ej: estilo agresivo, close-up del producto..."
                                className="flex w-full rounded-md border bg-white px-3 py-2 ring-offset-white placeholder:text-gray-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 min-h-[48px] resize-none border-gray-200/60 text-xs"
                                rows={2}
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

